import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './FitToTAUpload.css';
import { Box, Typography, Container, CircularProgress, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DatasetIcon from '@mui/icons-material/Dataset';
import axios from 'axios';
import PageTitle from '../../components/PageTitle/PageTitle';
import FitToTAUploadExcel from '../../components/FitToTAUploadExcel/FitToTAUploadExcel';

// Change to your actual base endpoint
const base_url = 'https://jasperd.ca';

const FitToTAUpload = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fitToTAExcelRef = useRef(null);

  // Called by FitToTAUploadExcel whenever a user selects/drops a file
  const handleFileSelected = async (file) => {
    // Immediately upload the file (no client-side parsing)
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Post the file to the backend
      const response = await axios.post(`${base_url}/api/submit-fit-to-ta-xls`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: () => true,
      });

      if (response.status === 202) {
        // We assume the server returns a task_id for polling
        const { task_id } = response.data;
        pollTaskStatus(task_id);
      } else {
        console.error('Error uploading file:', response.data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error submitting file:', error);
      setLoading(false);
    }
  };

  // Poll for Celery/Backend Task Status
  const pollTaskStatus = (taskId) => {
    const interval = setInterval(() => {
      axios
        .get(`${base_url}/api/task-status/${taskId}`, { validateStatus: () => true })
        .then((res) => {
          const result = res.data; // Convert the string to a JSON object
          console.log('Task status:', result);
          if (result.status === 'completed') {
            clearInterval(interval);
            setLoading(false);

            // The backend returns `result` as a dict from Redis
            const pacingData = result?.result || {};
            console.log('Pacing Data:', pacingData);

            // Navigate to /analysis with the data in location.state
            navigate('/tpm/tools/fit-to-ta/analysis', { state: { pacingData } });
          } else if (result.status === 'error') {
            clearInterval(interval);
            console.error('Task failed:', result.error);
            setLoading(false);
          }
          // otherwise keep polling until completed/error
        })
        .catch((pollErr) => {
          console.error('Error fetching task status:', pollErr);
          clearInterval(interval);
          setLoading(false);
        });
    }, 3000);
  };

  // (Optional) Download the template
  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/Fit-To-TA Inputs.xlsx`;
    link.download = 'Fit-To-TA Inputs.xlsx';
    link.click();
  };

  // (Optional) Use Example Data
  const handleUseExampleData = () => {
    axios
      .get(`${process.env.PUBLIC_URL}/Fit-To-TA Inputs.xlsx`, { responseType: 'blob' })
      .then((response) => {
        const file = new File([response.data], 'Fit-To-TA Inputs.xlsx', { type: response.data.type });
        // We can pass this file to FitToTAUploadExcel’s .processExternalFile if we want to simulate a user drop
        fitToTAExcelRef.current.processExternalFile(file);
      })
      .catch((error) => {
        console.error('Error using example data:', error);
      });
  };

  return (
    <div className="fittoTA-upload-screen">
      <Container className="fittoTA-upload-container">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <PageTitle 
            title="Fit Cashflows to Pacing Model"
            subtitle="Upload"
          />
        </Box>

        <Box sx={{ my: 3 }}>
          {/* 
            FitToTAUploadExcel handles drag-and-drop or file selection,
            then calls handleFileSelected(file) 
          */}
          <FitToTAUploadExcel
            ref={fitToTAExcelRef}
            onFileSelected={handleFileSelected}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        <Box
          sx={{ display: 'flex', justifyContent: 'center', gap: 2, my: 3, flexWrap: 'wrap' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ minWidth: '200px' }}
          >
            Download Excel Template
          </Button>
          <Button
            variant="contained"
            startIcon={<DatasetIcon />}
            onClick={handleUseExampleData}
            sx={{ minWidth: '200px' }}
          >
            Use Example Data
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default FitToTAUpload;